import { ReactElement } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { ControlledPasswordField } from '@/common/components/Fields/ControlledPasswordField/ControlledPasswordField';
import { PasswordField } from '@/common/components/Fields/PasswordField/PasswordField';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { PasswordValidatorType } from '@/common/types/general';
import { getPasswordValidation } from '@/common/utils/password';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ICreatePasswordFormData,
  createPasswordFormSchema,
} from './CreatePasswordForm.schema';

export const useCreatePasswordForm = (token: string) => {
  const methods = useForm<ICreatePasswordFormData>({
    mode: 'onTouched',
    defaultValues: {
      password: '',
      passwordConfirmation: '',
      token,
    },
    resolver: zodResolver(createPasswordFormSchema),
  });

  return { methods };
};

export function CreatePasswordFormFields() {
  const { t } = useTranslation();
  return (
    <Stack gap="md">
      <Controller
        name="password"
        render={({ field: { ref, ...field }, fieldState }) => {
          const passwordHint = getPasswordHint(field.value);
          return (
            <PasswordField
              {...field}
              id="password"
              label={t('authentication.create_password.password.label')}
              hint={passwordHint}
              error={fieldState.error?.message ? passwordHint : ''}
            />
          );
        }}
      />
      <ControlledPasswordField
        label={t('authentication.create_password.password_confirmation.label')}
        name="passwordConfirmation"
      />
    </Stack>
  );
}

export const getPasswordHint = (password: string) => {
  const passwordHintClass = (regex: PasswordValidatorType) => {
    if (!password) return 'text-neutral20';
    return getPasswordValidation(password).validRegexes.includes(regex)
      ? 'text-systemSuccess'
      : 'text-systemError';
  };
  const validators: PasswordValidatorType[] = [
    'length',
    'uppercase',
    'special_char',
    'lowercase',
  ];
  const components = validators.reduce(
    (acc, validator, idx) => {
      acc[idx + 1] = (
        <BodyText
          as="span"
          size="sm"
          className={passwordHintClass(validator)}
        />
      );
      return acc;
    },
    {} as Record<number, ReactElement>
  );

  return (
    <BodyText size="sm" color="neutral20">
      <Translate
        i18nKey="authentication.create_password.hint"
        components={components}
      />
    </BodyText>
  );
};
